import React, { useState } from 'react';

// AI Immigration Expert - Step 4: Personalized Improvement Recommendations
export const AIExpertStep4Recommendations = ({ formData, setFormData, onNext, onBack }) => {
  const [recommendations, setRecommendations] = useState(formData.recommendations || []);
  const [selectedActions, setSelectedActions] = useState(formData.selectedActions || []);

  React.useEffect(() => {
    if (recommendations.length === 0) {
      // Generate recommendations based on actual analysis results
      const analysisResults = formData.analysisResults || {};
      const riskResults = formData.riskResults || {};
      const uploadedFiles = formData.uploadedFiles || [];
      
      const generatedRecommendations = generateCaseSpecificRecommendations(
        analysisResults, 
        riskResults, 
        uploadedFiles
      );
      
      setRecommendations(generatedRecommendations);
      setFormData({ ...formData, recommendations: generatedRecommendations });
    }
  }, [formData.analysisResults, formData.riskResults]);

  const generateCaseSpecificRecommendations = (analysis, risks, files) => {
    const recommendations = [];
    let idCounter = 1;

    // Analyze what's actually missing based on uploaded files
    const categories = analysis.evidence_categories || {};
    const missingEssentials = analysis.missing_essentials || [];
    const detailedRisks = risks.detailed_risks || [];

    // Generate recommendations based on missing documents
    Object.entries(categories).forEach(([categoryName, categoryData]) => {
      if (categoryData.rating === '❌') { // Missing category
        recommendations.push({
          id: idCounter++,
          priority: 'High',
          impact: 'Critical',
          category: 'Missing Documents',
          title: `Upload ${categoryName}`,
          description: `Your case is missing ${categoryName.toLowerCase()} which is required for your application`,
          action: `Upload documents for ${categoryName.toLowerCase()} category`,
          estimatedTime: getCategoryTimeEstimate(categoryName),
          difficulty: getCategoryDifficulty(categoryName)
        });
      } else if (categoryData.rating === '⚠️') { // Weak category
        recommendations.push({
          id: idCounter++,
          priority: 'Medium',
          impact: 'High',
          category: 'Document Quality',
          title: `Strengthen ${categoryName}`,
          description: `Your ${categoryName.toLowerCase()} could be strengthened with additional documents`,
          action: `Add more supporting documents for ${categoryName.toLowerCase()}`,
          estimatedTime: getCategoryTimeEstimate(categoryName),
          difficulty: 'Medium'
        });
      }
    });

    // Generate recommendations based on specific risks
    detailedRisks.forEach(risk => {
      if (risk.severity === 'High' || risk.severity === 'Medium') {
        recommendations.push({
          id: idCounter++,
          priority: risk.severity === 'High' ? 'High' : 'Medium',
          impact: risk.severity === 'High' ? 'Critical' : 'High',
          category: 'Risk Mitigation',
          title: `Address ${risk.type}`,
          description: risk.description,
          action: risk.recommendation,
          estimatedTime: getRiskTimeEstimate(risk.type),
          difficulty: getRiskDifficulty(risk.type)
        });
      }
    });

    // Generate recommendations based on missing essentials
    missingEssentials.forEach(essential => {
      recommendations.push({
        id: idCounter++,
        priority: 'High',
        impact: 'High',
        category: 'Essential Documents',
        title: `Obtain ${essential}`,
        description: `This document is essential for your application and is currently missing`,
        action: `Obtain and upload ${essential.toLowerCase()}`,
        estimatedTime: getEssentialTimeEstimate(essential),
        difficulty: getEssentialDifficulty(essential)
      });
    });

    // Generate recommendations based on overall case strength
    const overallStrength = analysis.overall_strength || 0;
    if (overallStrength < 70) {
      recommendations.push({
        id: idCounter++,
        priority: 'High',
        impact: 'Critical',
        category: 'Case Strength',
        title: 'Improve Overall Case Strength',
        description: `Your case strength is ${overallStrength}% which is below the recommended 70% threshold`,
        action: 'Focus on addressing high-priority missing documents and risks',
        estimatedTime: '1-2 weeks',
        difficulty: 'Medium'
      });
    }

    // Add quality-specific recommendations
    const quality = analysis.quality_assessment || {};
    if (quality.readability < 80) {
      recommendations.push({
        id: idCounter++,
        priority: 'Medium',
        impact: 'Medium',
        category: 'Document Quality',
        title: 'Improve Document Readability',
        description: 'Some documents may have quality issues affecting readability',
        action: 'Re-scan documents at higher resolution or provide clearer copies',
        estimatedTime: '2-4 hours',
        difficulty: 'Easy'
      });
    }

    // Limit recommendations and sort by priority
    return recommendations
      .sort((a, b) => {
        const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      })
      .slice(0, 8); // Limit to top 8 recommendations
  };

  const getCategoryTimeEstimate = (category) => {
    switch (category) {
      case 'Identity Documents': return '1-2 hours';
      case 'Financial Evidence': return '3-5 days';
      case 'Relationship Evidence': return '2-7 days';
      case 'Employment Documents': return '1-3 days';
      case 'Government Forms': return '2-4 hours';
      default: return '1-2 days';
    }
  };

  const getCategoryDifficulty = (category) => {
    switch (category) {
      case 'Identity Documents': return 'Easy';
      case 'Financial Evidence': return 'Medium';
      case 'Relationship Evidence': return 'Medium';
      case 'Employment Documents': return 'Easy';
      case 'Government Forms': return 'Hard';
      default: return 'Medium';
    }
  };

  const getRiskTimeEstimate = (riskType) => {
    switch (riskType) {
      case 'Missing ID': return '1-2 hours';
      case 'Missing Financial Evidence': return '3-5 days';
      case 'Date Gaps': return '1-2 weeks';
      case 'Incomplete Package': return '1-2 weeks';
      default: return '3-7 days';
    }
  };

  const getRiskDifficulty = (riskType) => {
    switch (riskType) {
      case 'Missing ID': return 'Easy';
      case 'Missing Financial Evidence': return 'Medium';
      case 'Date Gaps': return 'Hard';
      case 'Incomplete Package': return 'Medium';
      default: return 'Medium';
    }
  };

  const getEssentialTimeEstimate = (essential) => {
    if (essential.includes('bank') || essential.includes('tax')) return '3-5 days';
    if (essential.includes('employment')) return '1-3 days';
    if (essential.includes('ID') || essential.includes('passport')) return '1-2 hours';
    if (essential.includes('marriage') || essential.includes('certificate')) return '1-2 weeks';
    return '3-7 days';
  };

  const getEssentialDifficulty = (essential) => {
    if (essential.includes('bank') || essential.includes('tax')) return 'Medium';
    if (essential.includes('employment')) return 'Easy';
    if (essential.includes('ID') || essential.includes('passport')) return 'Easy';
    if (essential.includes('marriage') || essential.includes('certificate')) return 'Hard';
    return 'Medium';
  };

  const toggleAction = (actionId) => {
    const updatedActions = selectedActions.includes(actionId)
      ? selectedActions.filter(id => id !== actionId)
      : [...selectedActions, actionId];
    
    setSelectedActions(updatedActions);
    setFormData({ ...formData, selectedActions: updatedActions });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'text-red-400 bg-red-900/20';
      case 'Medium': return 'text-yellow-400 bg-yellow-900/20';
      case 'Low': return 'text-green-400 bg-green-900/20';
      default: return 'text-gray-400 bg-gray-900/20';
    }
  };

  const getDifficultyIcon = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return '🟢';
      case 'Medium': return '🟡';
      case 'Hard': return '🔴';
      default: return '⚪';
    }
  };

  const getSelectedCount = () => selectedActions.length;
  const getHighPrioritySelected = () => 
    recommendations.filter(r => r.priority === 'High' && selectedActions.includes(r.id)).length;

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">💡 Personalized Improvement Recommendations</h3>
        <p className="text-gray-400 text-lg">AI-generated suggestions ranked by impact and priority</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-400">{recommendations.length}</div>
          <p className="text-gray-400">Total Recommendations</p>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-green-400">{getSelectedCount()}</div>
          <p className="text-gray-400">Actions Selected</p>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-red-400">{getHighPrioritySelected()}</div>
          <p className="text-gray-400">High Priority Selected</p>
        </div>
      </div>

      {/* Recommendations List */}
      <div className="space-y-4">
        {recommendations.map((rec) => (
          <div key={rec.id} className="bg-gray-800 p-6 rounded-lg border-l-4 border-blue-500">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <span className={`px-2 py-1 rounded text-xs font-semibold mr-3 ${getPriorityColor(rec.priority)}`}>
                    {rec.priority} Priority
                  </span>
                  <span className="text-gray-400 text-sm">{rec.category}</span>
                  <span className="ml-2">{getDifficultyIcon(rec.difficulty)}</span>
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">{rec.title}</h4>
                <p className="text-gray-400 mb-3">{rec.description}</p>
                
                <div className="bg-gray-700 p-3 rounded-lg mb-3">
                  <p className="text-blue-400 font-medium mb-1">Recommended Action:</p>
                  <p className="text-white text-sm">{rec.action}</p>
                </div>

                <div className="flex items-center text-sm text-gray-400">
                  <span className="mr-4">⏱️ {rec.estimatedTime}</span>
                  <span className="mr-4">📊 {rec.impact} Impact</span>
                  <span>{getDifficultyIcon(rec.difficulty)} {rec.difficulty}</span>
                </div>
              </div>
              
              <div className="ml-4">
                <button
                  onClick={() => toggleAction(rec.id)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                    selectedActions.includes(rec.id)
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {selectedActions.includes(rec.id) ? '✓ Selected' : 'Select Action'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Action Plan Summary */}
      {selectedActions.length > 0 && (
        <div className="bg-blue-900/20 border border-blue-500 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-white mb-4">📋 Your Action Plan</h4>
          <p className="text-gray-300 mb-4">
            You've selected {selectedActions.length} recommendations to implement. 
            Focus on high-priority items first for maximum case improvement.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-400 mb-2">High Priority Actions:</p>
              {recommendations.filter(r => r.priority === 'High' && selectedActions.includes(r.id))
                .map(r => (
                  <p key={r.id} className="text-red-400 text-sm">• {r.title}</p>
                ))}
            </div>
            <div>
              <p className="text-sm text-gray-400 mb-2">Quick Wins (Easy + High Impact):</p>
              {recommendations.filter(r => r.difficulty === 'Easy' && r.impact === 'High' && selectedActions.includes(r.id))
                .map(r => (
                  <p key={r.id} className="text-green-400 text-sm">• {r.title}</p>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
        >
          Continue to Q&A Session →
        </button>
      </div>
    </div>
  );
};

// AI Immigration Expert - Step 5: Interactive Q&A & Document Upload Cycle
export const AIExpertStep5QASession = ({ formData, setFormData, onNext, onBack }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState(formData.qaAnswers || {});
  const [additionalUploads, setAdditionalUploads] = useState(formData.additionalUploads || []);
  const [sessionComplete, setSessionComplete] = useState(false);

  const questions = [
    {
      id: 'missing_docs',
      question: 'What evidence do you have access to that you haven\'t uploaded yet?',
      type: 'multiselect',
      options: [
        'Additional bank statements',
        'Employment authorization documents',
        'Lease agreements or mortgage documents',
        'Tax transcripts from IRS',
        'Medical examination results',
        'Police clearance certificates',
        'Birth certificates',
        'Marriage certificates from other jurisdictions'
      ]
    },
    {
      id: 'document_access',
      question: 'Can you obtain the missing financial evidence before your filing deadline?',
      type: 'radio',
      options: ['Yes, within 1 week', 'Yes, within 2-4 weeks', 'Possibly, but it may take time', 'No, not available']
    },
    {
      id: 'affidavit_writers',
      question: 'Who can write affidavits supporting your case?',
      type: 'multiselect',
      options: [
        'US citizen spouse/family member',
        'Permanent resident friends',
        'US citizen friends',
        'Current or former employers',
        'Community organization members',
        'Religious organization leaders',
        'Medical professionals',
        'Other professionals who know you'
      ]
    },
    {
      id: 'timeline_concerns',
      question: 'Are there any gaps or concerns in your timeline that need explanation?',
      type: 'textarea',
      placeholder: 'Describe any periods of unemployment, travel, address changes, or other timeline issues...'
    },
    {
      id: 'additional_concerns',
      question: 'Do you have any other concerns about your case that the AI should analyze?',
      type: 'textarea',
      placeholder: 'Describe any specific worries, complex situations, or unique circumstances...'
    }
  ];

  const handleAnswer = (questionId, answer) => {
    const updatedAnswers = { ...answers, [questionId]: answer };
    setAnswers(updatedAnswers);
    setFormData({ ...formData, qaAnswers: updatedAnswers });
  };

  const handleFileUpload = async (files) => {
    const newFiles = [];
    
    for (const file of Array.from(files)) {
      try {
        // Convert file to base64 for OCR analysis
        const fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        console.log(`🔍 Analyzing document: ${file.name} (${file.type}, ${file.size} bytes)`);

        // Call backend for actual document analysis with Google Vision OCR
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const response = await fetch(`${backendUrl}/api/ai/analyze-uploaded-document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            document_file: fileData,
            document_name: file.name,
            document_type: 'additional-evidence',
            case_context: {
              case_type: 'AI Immigration Expert Additional Evidence',
              agent_type: 'ai-immigration-expert',
              applicant: 'Immigration Case Review',
              analysis_type: 'additional-evidence-review'
            }
          })
        });

        let documentAnalysis;
        if (response.ok) {
          documentAnalysis = await response.json();
          console.log(`✅ Document analysis successful for ${file.name}:`, documentAnalysis);
        } else {
          const errorText = await response.text();
          console.error(`❌ Document analysis failed for ${file.name}:`, response.status, errorText);
          documentAnalysis = {
            document_type: 'additional-evidence',
            content_description: `Document analysis failed: ${errorText}`,
            case_relevance: "Manual review required to determine relevance",
            exhibit_description: `Exhibit: ${file.name} requiring detailed manual review`,
            immigration_value: "Medium",
            analysis_error: `Analysis failed: ${errorText}`
          };
        }

        // Create file object with OCR analysis results
        const newFile = {
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          uploadDate: new Date().toISOString(),
          file: file,
          category: 'Additional Evidence',
          // Add OCR analysis results
          analysis: documentAnalysis,
          content_description: documentAnalysis.content_description,
          parties_mentioned: documentAnalysis.parties_mentioned,
          key_dates: documentAnalysis.key_dates,
          case_relevance: documentAnalysis.case_relevance,
          exhibit_description: documentAnalysis.exhibit_description,
          immigration_value: documentAnalysis.immigration_value,
          red_flags: documentAnalysis.red_flags,
          authenticity_indicators: documentAnalysis.authenticity_indicators,
          extracted_text: documentAnalysis.extracted_text
        };

        newFiles.push(newFile);
        
      } catch (error) {
        console.error('Document analysis error:', error);
        
        // Add file with error indication
        const newFile = {
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          uploadDate: new Date().toISOString(),
          file: file,
          category: 'Additional Evidence',
          analysis_error: "Analysis failed - manual review required",
          content_description: `Document analysis failed for ${file.name}`,
          case_relevance: "Manual review required",
          exhibit_description: `Exhibit: ${file.name} (manual review required)`,
          immigration_value: "Unknown"
        };
        
        newFiles.push(newFile);
      }
    }
    
    const updatedFiles = [...additionalUploads, ...newFiles];
    setAdditionalUploads(updatedFiles);
    setFormData({ ...formData, additionalUploads: updatedFiles });
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setSessionComplete(true);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const currentQ = questions[currentQuestion];

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">❓ Interactive Q&A Session</h3>
        <p className="text-gray-400 text-lg">Answer follow-up questions to strengthen your case</p>
      </div>

      {!sessionComplete ? (
        <>
          {/* Progress Indicator */}
          <div className="bg-gray-800 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400">Question {currentQuestion + 1} of {questions.length}</span>
              <span className="text-gray-400">{Math.round(((currentQuestion + 1) / questions.length) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Current Question */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">{currentQ.question}</h4>
            
            {currentQ.type === 'multiselect' && (
              <div className="space-y-2">
                {currentQ.options.map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={(answers[currentQ.id] || []).includes(option)}
                      onChange={(e) => {
                        const currentAnswers = answers[currentQ.id] || [];
                        const newAnswers = e.target.checked
                          ? [...currentAnswers, option]
                          : currentAnswers.filter(a => a !== option);
                        handleAnswer(currentQ.id, newAnswers);
                      }}
                      className="mr-3 text-blue-600"
                    />
                    <span className="text-gray-300">{option}</span>
                  </label>
                ))}
              </div>
            )}

            {currentQ.type === 'radio' && (
              <div className="space-y-2">
                {currentQ.options.map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name={currentQ.id}
                      checked={answers[currentQ.id] === option}
                      onChange={() => handleAnswer(currentQ.id, option)}
                      className="mr-3 text-blue-600"
                    />
                    <span className="text-gray-300">{option}</span>
                  </label>
                ))}
              </div>
            )}

            {currentQ.type === 'textarea' && (
              <textarea
                value={answers[currentQ.id] || ''}
                onChange={(e) => handleAnswer(currentQ.id, e.target.value)}
                placeholder={currentQ.placeholder}
                rows="4"
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
              />
            )}
          </div>

          {/* Navigation for Questions */}
          <div className="flex justify-between">
            <button
              onClick={prevQuestion}
              disabled={currentQuestion === 0}
              className={`px-6 py-3 rounded-lg transition-colors ${
                currentQuestion === 0
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-700 text-white hover:bg-gray-600'
              }`}
            >
              ← Previous
            </button>
            <button
              onClick={nextQuestion}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {currentQuestion === questions.length - 1 ? 'Complete Q&A' : 'Next →'}
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Session Complete */}
          <div className="bg-green-900/20 border border-green-500 p-6 rounded-lg text-center">
            <div className="text-4xl mb-4">✅</div>
            <h4 className="text-xl font-semibold text-white mb-2">Q&A Session Complete</h4>
            <p className="text-gray-400">Your responses will be used to provide more targeted recommendations</p>
          </div>

          {/* Upload Additional Documents */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-lg font-semibold text-white mb-4">📎 Upload Additional Documents</h4>
            <p className="text-gray-400 mb-4">Based on your answers, you can upload any additional evidence now:</p>
            
            <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center">
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => handleFileUpload(e.target.files)}
                className="hidden"
                id="additional-upload"
              />
              <label
                htmlFor="additional-upload"
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg cursor-pointer hover:bg-blue-700 transition-colors"
              >
                Choose Additional Files
              </label>
            </div>

            {additionalUploads.length > 0 && (
              <div className="mt-4">
                <p className="text-white font-medium mb-2">Uploaded ({additionalUploads.length}):</p>
                <div className="space-y-2">
                  {additionalUploads.map((file) => (
                    <div key={file.id} className="flex items-center bg-gray-700 p-2 rounded">
                      <span className="text-blue-400 mr-2">📄</span>
                      <span className="text-white">{file.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Final Navigation */}
      {sessionComplete && (
        <div className="flex justify-between">
          <button
            onClick={onBack}
            className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            ← Back
          </button>
          <button
            onClick={onNext}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            Continue to Document Preparation →
          </button>
        </div>
      )}
    </div>
  );
};